import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/enhanced-button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { resendVerification, validateSession } from "@/services/auth.service";
import { useResendCooldown } from "@/hooks/useResendCooldown";

const BROADCAST_CHANNEL = "blinkbooks_auth";
const ONBOARDING_PATH = "/onboarding/organization";
const POLL_INTERVAL_MS = 4000;

const VerifyEmailPending = () => {
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { cooldown, startCooldown } = useResendCooldown();

  const stateEmail = (location.state as { email?: string } | null)?.email;
  const [manualEmail, setManualEmail] = useState("");
  const email = stateEmail || manualEmail;

  // Listen for verification completion from the other tab, and poll as fallback.
  useEffect(() => {
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let bc: BroadcastChannel | null = null;

    const onVerified = () => {
      navigate(ONBOARDING_PATH, { replace: true });
    };

    // BroadcastChannel — instant notification when Tab 2 verifies
    try {
      bc = new BroadcastChannel(BROADCAST_CHANNEL);
      bc.onmessage = (event) => {
        if (event.data?.type === "email_verified") {
          onVerified();
        }
      };
    } catch {
      // Not supported — fall through to polling only
    }

    // Polling fallback — catches cases where BroadcastChannel is unavailable
    // or the user verified on a different device/browser.
    pollTimer = setInterval(async () => {
      const session = await validateSession();
      if (session) {
        onVerified();
      }
    }, POLL_INTERVAL_MS);

    return () => {
      bc?.close();
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [navigate]);

  const handleResend = async () => {
    if (!email) return;
    setIsResending(true);
    try {
      await resendVerification(email);
      startCooldown();
      toast({
        title: "Email sent",
        description: "A new verification email has been sent. Please check your inbox.",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to resend verification email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur-sm">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4">
              <img src="/content/logo.png" alt="BlinkBooks Logo" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Check your inbox</h1>
            <p className="text-muted-foreground mb-2">
              We sent a verification link to
            </p>
            {stateEmail ? (
              <p className="font-medium text-foreground mb-6">{stateEmail}</p>
            ) : (
              <input
                type="email"
                placeholder="Enter your email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring mb-6"
              />
            )}
            <p className="text-muted-foreground text-sm mb-2">
              Click the link in the email to verify your account and get started. The link expires in 30 minutes.
            </p>
            <p className="text-xs text-muted-foreground/70 mb-8 flex items-center justify-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary/50 animate-pulse" />
              This page will update automatically once you verify.
            </p>

            {(stateEmail || manualEmail) && (
              <Button
                variant="outline"
                className="w-full mb-4"
                onClick={handleResend}
                disabled={isResending || cooldown > 0}
              >
                {isResending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : cooldown > 0 ? (
                  `Resend in ${cooldown}s`
                ) : (
                  "Resend verification email"
                )}
              </Button>
            )}

            <button
              onClick={() => navigate("/login")}
              className="text-sm text-muted-foreground underline hover:text-foreground transition-colors"
            >
              Back to sign in
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPending;
